import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Lock, User, ArrowRight, Wine } from "lucide-react";
import { categoriaPuntuacion } from "@/lib/tastingOptions";
import ReactionBar from "@/components/ReactionBar";

const SORTS = [
  { value: "recientes", label: "Más recientes" },
  { value: "valoradas", label: "Mejor valoradas" },
  { value: "reaccionadas", label: "Más reaccionadas" },
];

export default function Community() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const active = user?.subscription?.active || user?.is_admin;
  const [sort, setSort] = useState("recientes");
  const [tipo, setTipo] = useState("todos");

  const { data: tipos = [] } = useQuery({
    queryKey: ["community-tipos"],
    queryFn: async () => (await api.get("/community/tipos")).data,
    enabled: !!active,
    retry: false,
  });

  const { data: feed, isLoading, isError } = useQuery({
    queryKey: ["community-feed", sort, tipo],
    queryFn: async () => (await api.get("/community/tastings", { params: { sort, tipo } })).data,
    enabled: !!active,
    retry: false,
  });

  if (!active) {
    return (
      <div className="max-w-xl mx-auto text-center py-16 animate-fade-up" data-testid="community-locked">
        <span className="inline-flex w-16 h-16 rounded-2xl bg-borgona-50 items-center justify-center mb-6">
          <Lock className="w-8 h-8 text-borgona-700" />
        </span>
        <h1 className="font-serif text-3xl text-negro mb-3">Catas Compartidas</h1>
        <p className="text-grises-500 mb-8">
          La comunidad de Catas Compartidas está disponible solo para usuarios con una suscripción activa.
          Descubre las catas de otros amantes del vino y reacciona a sus notas.
        </p>
        <Button onClick={() => navigate("/app")} className="bg-borgona-700 hover:bg-borgona-900 text-blanco rounded-full">
          Volver al panel
        </Button>
      </div>
    );
  }

  return (
    <div className="animate-fade-up" data-testid="community-page">
      <div className="flex items-center gap-3 mb-2">
        <Users className="w-7 h-7 text-borgona-700" strokeWidth={1.75} />
        <h1 className="font-serif text-4xl text-negro">Catas Compartidas</h1>
      </div>
      <p className="text-grises-500 mb-6">Las mejores catas de la comunidad Vinonotas. Reacciona a las que más te gusten.</p>

      <div className="flex flex-wrap gap-3 mb-8">
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger data-testid="community-sort" className="h-10 w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            {SORTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={tipo} onValueChange={setTipo}>
          <SelectTrigger data-testid="community-tipo" className="h-10 w-48"><SelectValue placeholder="Tipo de vino" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los tipos</SelectItem>
            {tipos.map((tp) => <SelectItem key={tp} value={tp}>{tp}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {isLoading && <p className="text-grises-400">Cargando comunidad...</p>}
      {isError && <p className="text-grises-400">No se pudo cargar la comunidad.</p>}
      {feed && feed.length === 0 && (
        <Card className="p-10 text-center border-grises-200 rounded-xl">
          <Wine className="w-10 h-10 text-grises-300 mx-auto mb-3" />
          <p className="text-grises-500">Aún no hay catas compartidas. ¡Sé el primero en compartir una marcándola como pública!</p>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {feed?.map((t) => {
          const cat = categoriaPuntuacion(t.puntuacion);
          const w = t.wine || {};
          return (
            <Card key={t.id} data-testid={`community-card-${t.id}`} className="p-5 border-grises-200 rounded-xl hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-serif text-xl text-negro truncate">{w.nombre || "Vino"}</h3>
                  <p className="text-sm text-grises-500 truncate">{[w.bodega, w.pais, w.variedad, w.anada].filter(Boolean).join(" · ")}</p>
                </div>
                <div className="text-center shrink-0">
                  <div className="font-mono text-3xl font-bold" style={{ color: cat.color }}>{t.puntuacion}</div>
                  <div className="text-[10px] uppercase tracking-wider font-medium" style={{ color: cat.color }}>{cat.label}</div>
                </div>
              </div>

              <p className="text-sm text-borgona-700 mt-2 flex items-center gap-1.5">
                <User className="w-3.5 h-3.5" /> {t.author}
                {t.fecha_cata && <span className="text-grises-400">· {t.fecha_cata}</span>}
              </p>

              {t.observaciones?.notas && (
                <p className="text-sm text-grises-600 mt-3 line-clamp-3">{t.observaciones.notas}</p>
              )}

              <div className="flex items-center justify-between gap-3 mt-4 pt-4 border-t border-grises-100">
                <ReactionBar tastingId={t.id} reactions={t.reactions} size="sm" />
                <button
                  data-testid={`community-open-${t.id}`}
                  onClick={() => navigate(`/app/comunidad/${t.id}`)}
                  className="text-sm text-borgona-700 font-medium inline-flex items-center gap-1 hover:gap-2 transition-all shrink-0"
                >
                  Ver ficha <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
